<template>
  <div class="w-full p-4 max-w-6xl">
    <!-- The band stays on screen, as on My Expenses. -mt-4 cancels the
         page's own p-4, which otherwise leaves 16px of slack before it
         catches. Measured, because the table headers below pin under it. -->
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Request Backlog</h2>
      </div>

      <p class="text-sm text-gray-500">
        Every request anyone has filed, beside the work that resolves it, in
        the order the team is working through it. Items the team has published
        appear too, including ones nobody asked for.
      </p>
    </div>

    <div v-if="loading" class="text-sm text-gray-500 mt-6">Loading…</div>
    <p v-else-if="loadError" class="text-sm text-red-600 mt-6">{{ loadError }}</p>

    <template v-else>
      <div class="flex items-end gap-4 mt-5">
        <label class="flex flex-col">
          <span class="text-xs text-gray-500 mb-1">Search</span>
          <input
            v-model="search"
            type="text"
            placeholder="Summary, department…"
            class="h-9 px-2 border border-gray-400 rounded-md text-sm w-64"
          />
        </label>
        <label class="flex flex-col">
          <span class="text-xs text-gray-500 mb-1">Show</span>
          <select v-model="scope" class="h-9 px-2 border border-gray-400 rounded-md text-sm">
            <option value="all">Everything</option>
            <option value="mine">Only my requests</option>
          </select>
        </label>
        <label class="flex items-center gap-2 pb-1">
          <input v-model="showClosed" type="checkbox" />
          <span class="text-sm">Show finished and declined</span>
        </label>
      </div>

      <!-- A table rather than cards: the whole point is comparing a row
           against the rows above and below it. -->
      <!-- No overflow wrapper: an overflow-* ancestor becomes the
           scrollport, and the sticky header below would then pin to the
           table rather than to the page. -->
      <div class="mt-5 border border-gray-400 rounded-md bg-white">
      <table class="table-fixed w-full text-sm">
        <colgroup>
          <col />
          <col class="w-36" />
          <col class="w-64" />
          <col class="w-24" />
          <col class="w-28" />
        </colgroup>
        <thead
          class="bg-dark-primary text-white sticky z-20"
          :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
        >
          <tr class="text-left">
            <th class="text-left px-3 py-3">The request</th>
            <th class="text-left px-3 py-3">State</th>
            <th class="text-left px-3 py-3">Being handled as</th>
            <th class="text-left px-3 py-3">Priority</th>
            <th class="text-left px-3 py-3">Expected</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in visibleRequests"
            :key="`r${row.id}`"
            class="border-t border-gray-200 align-top hover:bg-gray-50"
            :class="row.is_mine ? 'bg-amber-50 hover:bg-amber-100' : ''"
          >
            <td class="px-3 py-2">
              <div class="flex items-start gap-2">
                <span
                  v-if="row.is_mine"
                  class="text-xs px-2 py-0.5 rounded bg-amber-200 text-amber-900 shrink-0"
                >Yours</span>
                <div>
                  <!-- Your own wording, or the summary triage wrote for
                       everyone else. Never somebody else's raw words. -->
                  <p v-if="row.body" class="text-gray-800">{{ row.body }}</p>
                  <p v-else-if="row.public_summary" class="text-gray-800">
                    {{ row.public_summary }}
                  </p>
                  <p v-else class="text-gray-400 italic">
                    Not summarised yet
                  </p>
                  <p class="text-xs text-gray-500 mt-0.5">
                    <span v-if="row.reporter_display">{{ row.reporter_display }} · </span>
                    <span v-if="row.department_name">{{ row.department_name }} · </span>
                    filed {{ formatDateTimeWithSeconds(row.created_at) }}
                  </p>
                  <p
                    v-if="row.is_mine && row.public_summary"
                    class="text-xs text-gray-600 mt-1"
                  >{{ row.public_summary }}</p>
                  <p v-if="row.resolution_note" class="text-xs text-gray-600 mt-1">
                    {{ row.resolution_note }}
                  </p>
                  <!-- Read-only here: this page is for watching, and the
                       strip can only upload where the server would allow
                       it anyway. -->
                  <attachment-strip
                    v-if="row.attachments && row.attachments.length"
                    :attachments="row.attachments"
                    size="sm"
                  />
                </div>
              </div>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              <span class="text-xs px-2 py-0.5 rounded" :class="requestStatusChip(row.status)">
                {{ row.status_display }}
              </span>
              <div
                v-if="row.next_step_with === 'reporter'"
                class="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800 mt-1 inline-block"
              >{{ row.is_mine ? 'Over to you' : 'Waiting on the requester' }}</div>
            </td>
            <td class="px-3 py-2">
              <template v-if="row.work_item">
                <p v-if="row.work_item.public_summary" class="text-gray-800">
                  {{ row.work_item.public_summary }}
                </p>
                <!-- An item exists but has not been published, so there is
                     nothing public to quote. Its state is still real news. -->
                <p v-else class="text-gray-400 italic">On the backlog</p>
                <!-- A milestone is the kind written to be read out here,
                     so it is the one that names itself. Labelling every
                     story and task would just be noise to a requester. -->
                <span
                  v-if="row.work_item.kind === 'milestone'"
                  class="text-xs px-2 py-0.5 rounded mt-1 mr-1 inline-block"
                  :class="workItemKindClass(row.work_item.kind)"
                >{{ row.work_item.kind_display }}</span>
                <span class="text-xs px-2 py-0.5 rounded mt-1 inline-block"
                      :class="workItemStatusChip(row.work_item.status)">
                  {{ row.work_item.status_display }}
                </span>
                <attachment-strip
                  v-if="row.work_item.attachments && row.work_item.attachments.length"
                  :attachments="row.work_item.attachments"
                  size="sm"
                />
              </template>
              <span v-else class="text-gray-400 italic">Not planned yet</span>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              <span v-if="row.work_item && row.work_item.position" class="text-gray-700">
                {{ row.work_item.position }}
              </span>
              <!-- Requests are not ranked, only work items are. Saying
                   nothing beats inventing a position. -->
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              <!-- A milestone's target date is the firmest thing we have
                   said about when, so it wins over a per-request ETA. -->
              <span v-if="milestoneDate(row.work_item)" class="font-medium">
                {{ milestoneDate(row.work_item) }}
              </span>
              <span v-else-if="row.public_eta || (row.work_item && row.work_item.public_eta)">
                {{ row.public_eta || row.work_item.public_eta }}
              </span>
              <span v-else-if="row.estimate_display" class="text-gray-600">
                {{ row.estimate_display }}
              </span>
              <span v-else class="text-gray-400">—</span>
            </td>
          </tr>

          <tr v-if="!visibleRequests.length">
            <td colspan="5" class="px-3 py-4 text-center text-gray-500">
              No requests match these filters.
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <!-- Published work nobody filed a request for. Kept in its own table
           so the columns above keep meaning "the request" throughout. -->
      <template v-if="visiblePublished.length">
        <h3 class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mt-8">
          Also on the way
        </h3>
        <p class="text-sm text-gray-500 mt-1">
          Work the team published that no request asked for.
        </p>
        <div class="mt-3 border border-gray-400 rounded-md bg-white">
        <table class="table-fixed w-full text-sm">
          <colgroup>
            <col />
            <col class="w-36" />
            <col class="w-24" />
            <col class="w-28" />
          </colgroup>
          <thead
            class="bg-dark-primary text-white sticky z-20"
            :style="{ top: (topStickyHeight + NAVBAR_HEIGHT) + 'px' }"
          >
            <tr class="text-left">
              <th class="text-left px-3 py-3">What it is</th>
              <th class="text-left px-3 py-3">State</th>
              <th class="text-left px-3 py-3">Priority</th>
              <th class="text-left px-3 py-3">Expected</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in visiblePublished"
              :key="`i${item.id}`"
              class="border-t border-gray-200 align-top hover:bg-gray-50"
            >
              <td class="px-3 py-2">
                <span
                  v-if="item.kind === 'milestone'"
                  class="text-xs px-2 py-0.5 rounded mr-1 align-middle"
                  :class="workItemKindClass(item.kind)"
                >{{ item.kind_display }}</span>
                {{ item.public_summary }}
                <attachment-strip
                  v-if="item.attachments && item.attachments.length"
                  :attachments="item.attachments"
                  size="sm"
                />
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span class="text-xs px-2 py-0.5 rounded" :class="workItemStatusChip(item.status)">
                  {{ item.status_display }}
                </span>
              </td>
              <td class="py-2 pr-3 whitespace-nowrap text-gray-700">{{ item.position }}</td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span v-if="milestoneDate(item)" class="font-medium">
                  {{ milestoneDate(item) }}
                </span>
                <span v-else-if="item.public_eta">{{ item.public_eta }}</span>
                <span v-else class="text-gray-400">—</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { formatDateTimeWithSeconds } from '@/composables/formatDateTime'

import AttachmentStrip from '@/components/back-office/AttachmentStrip.vue'
import { useStickyOffset } from '@/composables/useStickyOffset'
import { getRequestBacklog } from '@/services/BackOffice'
import { REQUEST_CLOSED_STATUSES, requestStatusChip } from '@/utils/requestOptions'
import { workItemKindClass, workItemStatusChip } from '@/utils/workItemOptions'

const store = useStore()

// The app's navbar is fixed at this height, as on the backlog board.
const NAVBAR_HEIGHT = 56

// Measured rather than assumed: the band wraps at narrow widths, and a
// table header offset by a stale number either overlaps it or floats clear.
const topStickyRef = ref(null)
const topStickyHeight = useStickyOffset(topStickyRef)
const token = () => store.getters['auth/showToken']

const requests = ref([])
const publishedItems = ref([])
const loading = ref(true)
const loadError = ref('')

const search = ref('')
const scope = ref('all')
// Off by default: a finished request is no longer a question about when it
// lands, which is what this page is for.
const showClosed = ref(false)

function milestoneDate(item) {
  return item && item.kind === 'milestone' ? item.target_date : null
}

function matchesSearch(text) {
  const needle = search.value.trim().toLowerCase()
  if (!needle) return true
  return String(text || '').toLowerCase().includes(needle)
}

const visibleRequests = computed(() => requests.value.filter((row) => {
  if (scope.value === 'mine' && !row.is_mine) return false
  if (!showClosed.value && REQUEST_CLOSED_STATUSES.includes(row.status)) return false
  const haystack = [
    row.body,
    row.public_summary,
    row.department_name,
    row.reporter_display,
    row.work_item && row.work_item.public_summary,
  ].join(' ')
  return matchesSearch(haystack)
}))

const visiblePublished = computed(() => {
  // "Only my requests" is a question about requests; unrequested work is
  // not an answer to it.
  if (scope.value === 'mine') return []
  return publishedItems.value.filter(item => matchesSearch(item.public_summary))
})

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const payload = await getRequestBacklog(token())
    requests.value = payload.requests || []
    publishedItems.value = payload.published_items || []
  } catch (e) {
    loadError.value = describe(e)
  } finally {
    loading.value = false
  }
}

function describe(e) {
  if (e?.response?.status === 403) {
    return 'The Request Backlog is not open to you yet.'
  }
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(load)
</script>
